import { Col, Row } from "antd";
import classNames from "classnames";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import QuickActionRed from "shared/components/SVG/LMF/QuickActionRed";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM } from "utils/constants";
import { bindInputProps, bindWebTextFieldInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Footer from "../Components/Footer";
import Head from "../Components/Head";
import InfoBox from "../Components/InfoBox";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import InputChecked from "../Components/svg/check_small.svg";
import InputError from "../Components/svg/error.svg";
import validation from "./validation";

const View = ({ onNext, initialValues, locked, onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.ADHERENT_TELECOMS_LMF", {
    returnObjects: true,
  });

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues,
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });

  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <>
      <Layout>
        <Head
          description={TRANSLATION.DESCRIPTION}
          title={TRANSLATION.TITLE}
          onBack={onBack}
        />

        <div className="__LMF--content LMF-content lmf__form">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.LAST_NAME.LABEL}
                placeholder={TRANSLATION.FORM.LAST_NAME.PLACEHOLDER}
                className={classNames({
                  valid: get(values, "contact.user.lastname"),
                })}
                {...bindWebTextFieldInputProps({
                  name: `contact.user.lastname`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                suffix={
                  get(formProps.errors, `contact.user.lastname`) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.FIRST_NAME.LABEL}
                placeholder={TRANSLATION.FORM.FIRST_NAME.PLACEHOLDER}
                className={classNames({
                  valid: get(values, "contact.user.firstname"),
                })}
                {...bindWebTextFieldInputProps({
                  name: `contact.user.firstname`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                suffix={
                  get(formProps.errors, `contact.user.firstname`) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.PHONE.LABEL}
                placeholder={TRANSLATION.FORM.PHONE.PLACEHOLDER}
                inputMode="numeric"
                trim
                className={classNames({
                  valid: get(
                    values,
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                  ),
                })}
                {...bindInputProps({
                  name: `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                suffix={
                  get(
                    formProps.errors,
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                  ) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.EMAIL.LABEL}
                placeholder={TRANSLATION.FORM.EMAIL.PLACEHOLDER}
                className={classNames({
                  valid: get(
                    values,
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                  ),
                })}
                inputMode="numeric"
                {...bindInputProps({
                  name: `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={async ({ target: { value } }) => {
                  setFieldValue(
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                    value,
                  );
                }}
                suffix={
                  get(
                    formProps.errors,
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                  ) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={24}>
              <Input
                label={TRANSLATION.FORM.N_ADHERENT.LABEL}
                placeholder={TRANSLATION.FORM.N_ADHERENT.PLACEHOLDER}
                className={classNames({
                  valid: get(
                    values,
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.ADHERENT_NUMBER}.value`,
                  ),
                })}
                inputMode="numeric"
                {...bindInputProps({
                  name: `contact.telecoms.${CONTACT_POINT_SYSTEM.ADHERENT_NUMBER}.value`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                // onChange={async ({ target: { value } }) => {
                //   setFieldValue(
                //     `contact.telecoms.${CONTACT_POINT_SYSTEM.ADHERENT_NUMBER}.value`,
                //     value
                //   );
                // }}
                onChange={async ({ target: { value } }) => {
                  const formatted = value.replace(/\D/g, "").slice(0, 8);
                  setFieldValue(
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.ADHERENT_NUMBER}.value`,
                    formatted,
                  );
                }}
                suffix={
                  get(
                    formProps.errors,
                    `contact.telecoms.${CONTACT_POINT_SYSTEM.ADHERENT_NUMBER}.value`,
                  ) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
              />
            </Col>

            <Col xs={24} sm={24} md={24} lg={24}>
              <Input
                type="checkbox"
                label={TRANSLATION.FORM.OPT_INS.CONTACT}
                checked={get(values, "contact.fields.acceptContact")}
                {...bindInputProps({
                  name: "contact.fields.acceptContact",
                  values,
                  ...formProps,
                })}
                errors={[]}
                onChange={({ target: { checked } }) => {
                  setFieldValue("contact.fields.acceptContact", checked);
                }}
                disabled={locked}
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={24}>
              <Input
                type="checkbox"
                label={TRANSLATION.FORM.OPT_INS.MAIL_ME}
                checked={get(values, "contact.fields.acceptDigital")}
                {...bindInputProps({
                  name: "contact.fields.acceptDigital",
                  values,
                  ...formProps,
                })}
                errors={[]}
                onChange={({ target: { checked } }) => {
                  setFieldValue("contact.fields.acceptDigital", checked);
                }}
                disabled={locked}
              />
            </Col>
          </Row>

          <div className="pt-24" />
          <InfoBox
            variant="danger"
            text={TRANSLATION.FORM.INFO}
            icon={<QuickActionRed size={48} />}
          />
        </div>

        <ActionButtons
          disabled={
            !isEmpty(formProps.errors) || !get(values, "contact.user.firstname")
          }
          onNext={handleSubmit}
          buttonText={TRANSLATION.NEXT}
          className={classNames({
            "w-100": isMobile,
          })}
          {...(isMobile && { onBack })}
        />

        <Footer variant="center" size="small" />
      </Layout>
    </>
  );
};

export default View;
